// Tipos centrais do CRM OtimizCred
// Sessão 7C: PropostaBancaria, SistemaAmortizacao, StatusPropostaBancaria adicionados

// SPEC_LAYOUT_UX_V1 (E1, 06/07/2026) - board vira 3 esteiras visuais: 1=Crédito
// (agrupa ENTRADA "Cadastro" + CREDITO "análise bancária/Seleção de Banco" -
// enum Prisma preservado por baixo, ver Operacao.esteiraAtualEnum), 2=Jurídico
// (ex-OPERACIONAL), 3=Registro (inalterado).
export type Esteira = 0 | 1 | 2 | 3;

// Valor bruto do enum Esteira do Prisma - usado para desambiguar dentro do
// bucket visual 1 (Crédito), que agrupa dois valores de enum.
export type EsteiraEnum = 'COMERCIAL' | 'ENTRADA' | 'CREDITO' | 'OPERACIONAL' | 'REGISTRO';

// SPEC 1 - papel do usuário interno
export type PapelUsuario = 'ADMIN' | 'ANALISTA' | 'ATENDIMENTO';

// SPEC 1 - usuário cadastrado no CRM
export interface UsuarioCRM {
  id: string;
  email: string;
  nome: string;
  papel: PapelUsuario;
  ativo: boolean;
  tipo: 'HUMANO' | 'ROBO';
  azureOid?: string | null;
}

// ── Propostas bancárias (Sessão 7C) ──────────────────────────────────────────

// Paridade com o enum Banco do Prisma (schema.prisma) - BRADESCO/INTER adicionados
// 05/07/2026 no fix do mapeamento idFornecEmissao (achado: 3=BRADESCO na carteira real).
export type BancoPropostaBancaria = 'CAIXA' | 'ITAU' | 'SANTANDER' | 'BRADESCO' | 'INTER' | 'OUTRO';

export type StatusPropostaBancaria =
  | 'ENVIADA'
  | 'AGUARDANDO'
  | 'APROVADA'
  | 'REPROVADA'
  | 'PENDENTE_DOC'
  | 'CANCELADA';

export type SistemaAmortizacao = 'SAC' | 'PRICE';
export type Indexador = 'TR' | 'CDI';
export type ResultadoRodada = 'APROVADA' | 'APROVADA_PARCIAL' | 'REPROVADA';

// SPEC_BLOCO3 (14/07/2026) - versiona por RODADA o par solicitado×resposta
// dentro da mesma PropostaBancaria (substitui os campos ambíguos que existiam
// direto em PropostaBancaria - ver PASSAGEM_14-07_BLOCO3.md).
export interface RodadaProposta {
  id: string;
  numeroRodada: number;
  valorSolicitado?: number;
  prazoSolicitadoMeses?: number;
  dataEnvio?: string;
  valorAprovado?: number;
  prazoAprovadoMeses?: number;
  taxaNominalAa?: number;
  cetAa?: number;
  indexador?: Indexador;
  sistemaAmortizacao?: SistemaAmortizacao;
  dataResposta?: string;
  dataValidade?: string;
  resultado?: ResultadoRodada;
  canceladaEm?: string;
  origemBackfill: boolean;
}

export interface PropostaBancaria {
  id: string;
  operacaoId: string;
  banco: BancoPropostaBancaria;
  numeroNoBanco?: string;        // nº da proposta no banco
  numeroIsolve?: string;         // nº no iSolve
  status: StatusPropostaBancaria;
  // Campos gerais
  temIq: boolean;
  dpsSeguradora?: string;
  // Campos CAIXA (v3.1)
  codigoProposta?: string;
  codigoAvaliacao?: string;
  // SPEC_D (L01/E6) - programa é a chave de ramificação do Checklist D
  programa?: Programa | null;
  createdAt: string;
  updatedAt: string;
  // SPEC_BLOCO3 (14/07/2026) - ultimaRodada existe independente de resposta
  // (mostra "aguardando resposta" na UI); condicaoVigente (D6) só existe
  // quando a última rodada JÁ foi respondida.
  ultimaRodada?: RodadaProposta;
  condicaoVigente?: RodadaProposta;
}

// SPEC_BLOCO3 (14/07/2026) - payload de POST/PATCH propostas: o form único
// desta UI mínima não distingue solicitado×aprovado (ver PASSAGEM_14-07_BLOCO3.md)
// - valorFinanciado/prazoMeses/taxaJuros/sistemaAmortizacao não são mais campos
// de PropostaBancaria, o backend os roteia para a rodada 1 (POST) ou para a
// rodada aberta mais recente (PATCH).
export interface PropostaBancariaPayload
  extends Partial<Omit<PropostaBancaria, 'id' | 'operacaoId' | 'createdAt' | 'updatedAt' | 'ultimaRodada' | 'condicaoVigente'>> {
  valorFinanciado?: number;
  prazoMeses?: number;
  taxaJuros?: number;
  sistemaAmortizacao?: SistemaAmortizacao;
}

export type Programa = 'CCFGTS_PMCMV' | 'CCSBPE' | 'RECURSOS_LIVRES' | 'PRO_COTISTA';

export type StatusOperacao =
  | 'ATIVA'
  | 'CONGELADA'
  | 'CONCLUIDA'
  | 'CANCELADA';

export type OrigemPrefixo = '5A' | 'S4' | 'VAR';

export interface Pessoa {
  id: string;
  nome: string;
  cpf?: string;
  cnpj?: string;
  tipo: 'PF' | 'PJ';
  telefone?: string;
  email?: string;
  // 1 = proponente principal (comprador). Usado na ação de WhatsApp (aba Msgs).
  posicaoIsolve?: number | null;
}

export interface Banco {
  id: string;
  nome: string;
  status: 'PENDENTE' | 'APROVADO' | 'NEGADO' | 'CONGELADO';
  valor?: number;
  taxa?: number;
  prazo?: number;
  cet?: number;
  observacoes?: string;
}

export interface Documento {
  id: string;
  nome: string;
  tipo: string;
  status: 'PENDENTE' | 'RECEBIDO_AGUARDA_VALIDACAO' | 'VALIDADO' | 'REJEITADO' | 'AUSENTE' | 'TRIAGEM_MANUAL' | 'PROPOSTA_PENDENTE_APROVACAO';
  urlSharePoint?: string;
  observacoes?: string;
  origemIsolve?: boolean;
  // SPEC S2, ajuste pós-Fase-1 (INT-043) - proposta de triagem visível na aba Docs
  // enquanto o GATE E6 (TRIAGEM_HABILITADA) estiver fechado.
  tipoPropostoNome?: string;
  nomeArquivoProposto?: string;
  confiancaClassificacao?: number;
  // X4-a (15/07/2026, E5-bis) - true só quando TRIAGEM_MANUAL foi por falha de
  // TRANSPORTE (não ambiguidade de classificação); botão "Reprocessar triagem" só
  // aparece com este sinal.
  falhaTransporte?: boolean;
  updatedAt: string;
}

export interface Mensagem {
  id: string;
  canal: 'WHATSAPP' | 'EMAIL' | 'INTERNO';
  remetente: string;
  conteudo: string;
  lida: boolean;
  createdAt: string;
}

// Template de mensagem reutilizável (aba Mensagens - envio por WhatsApp)
export interface TemplateWhatsapp {
  id: string;
  nome: string;
  corpo: string;
}

// Central de Comunicação - comentário interno de uma operação (Fase A+B)
export interface ComentarioOperacao {
  id: string;
  corpo: string;
  createdAt: string;
  editadoEm: string | null;
  autor: { id: string; nome: string; email: string };
  mencoes: { usuario: { id: string; nome: string } }[];
}

// Usuário mencionável no autocomplete de @ (Fase B)
export interface UsuarioMencionavel {
  id: string;
  nome: string;
  email: string;
}

// Notificação do usuário logado (Fase B; destino das menções)
export type TipoNotificacao = 'MENCAO_COMENTARIO';
export interface Notificacao {
  id: string;
  tipo: TipoNotificacao;
  operacaoId: string | null;
  comentarioId: string | null;
  resumo: string;
  lidaEm: string | null;
  criadoEm: string;
}

export interface Tarefa {
  id: string;
  titulo: string;
  concluida: boolean;
  responsavel?: string;
  prazo?: string;
}

export interface MovimentacaoEsteira {
  id: string;
  estagioDE: string;
  estagioParaA: string;
  movidoEm: string;
  usuario: string;
}

// SPEC_ESTACAO1 (E6, 10/07/2026) - histórico de transição de UMA PropostaBancaria
// (GET /operacoes/:operacaoId/propostas/:id/movimentacoes). estagioDE="CRIACAO"
// é o registro de nascimento (fora da matriz), não uma transição de fato.
export interface MovimentacaoProposta {
  id: string;
  estagioDE: string;
  estagioParaA: string;
  movidoEm: string;
  motivo?: string;
  usuario: string;
}

// Matriz de transição de StatusPropostaBancaria (GET /operacoes/:operacaoId/
// propostas/matriz-transicao) - fonte única do backend, a UI nunca duplica a
// regra (MET-011/E6).
export interface MatrizTransicaoProposta {
  matriz: Record<StatusPropostaBancaria, StatusPropostaBancaria[]>;
  transicoesQueExigemMotivo: string[];
}

export interface ImovelDetalhe {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  ufImovel?: string;
  matricula?: string;
  matriculaDataValidade?: string;
  fgtsRestricao?: boolean;
  uso?: string;
}

export interface TimelineEvento {
  id: string;
  descricao: string;
  usuario: string;
  createdAt: string;
  tipo: 'STATUS' | 'MENSAGEM' | 'DOCUMENTO' | 'NOTA';
}

// SPEC S1 - Feed de Eventos + Farol (03/07/2026)
export type TipoEventoOperacao =
  | 'DADOS_ATUALIZADOS'
  | 'DOCUMENTOS_NOVOS'
  | 'DIVERGENCIA_ABERTA'
  | 'DIVERGENCIA_RESOLVIDA'
  | 'CCV_ASSINADO'
  | 'OPERACAO_IMPORTADA'
  // SPEC S3 (03/07/2026) - Crítica de CCV
  | 'CRITICA_CCV_CONCLUIDA';

// SPEC S3 - Crítica de CCV (03/07/2026)
export type StatusGeralCritica = 'LIMPA' | 'COM_DIVERGENCIAS' | 'FALHA_LEITURA';
export type CategoriaResultadoCritica = 'ok' | 'divergente' | 'nao_cadastrado' | 'baixa_confianca';

export interface ItemResultadoCritica {
  secao: 'parte' | 'imovel' | 'valores' | 'datas' | 'assinaturas';
  referencia?: string;
  campo: string;
  categoria: CategoriaResultadoCritica;
  valorCcv: unknown;
  valorCadastro: unknown;
  confianca: number;
}

export interface CriticaCCV {
  id: string;
  operacaoId: string;
  ccvArquivoId: string;
  extracao: Record<string, unknown>;
  resultado: ItemResultadoCritica[];
  statusGeral: StatusGeralCritica;
  tokensEntrada?: number;
  tokensSaida?: number;
  custoEstimado?: number;
  criadoEm: string;
}

// SPEC_FILA_CONSULTA_CADASTRAL - E5 (UI mínima da fila de consultas SICAQ)
export interface ItemFilaConsultaCadastral {
  parteId: string;
  nome: string;
  cpf: string | null;
  status: 'PENDENTE' | 'NADA_CONSTA' | 'COM_APONTAMENTOS';
  dataUltimaConsulta: string | null;
  serasaQtde: number | null;
  serasaValorTotal: number | null;
  scpcQtde: number | null;
  scpcValorTotal: number | null;
  documentoId: string | null;
  urlSharePoint: string | null;
}

// X4-b (15/07/2026, E5-bis) - documento SICAQ já triado (tipoDocumentoId confirmado)
// sem ConsultaCadastral ainda: sinal estrutural pro botão "Reextrair" (nível da
// operação, não do personagem - Documento não tem parteId próprio no schema).
export interface DocumentoSicaqSemExtracao {
  documentoId: string;
  nomeArquivo: string;
}

export interface FilaConsultaCadastralResposta {
  itens: ItemFilaConsultaCadastral[];
  documentosSemExtracao: DocumentoSicaqSemExtracao[];
}

export type SeveridadeEvento = 'INFO' | 'ACIONAVEL';

export interface EventoOperacao {
  id: string;
  tipo: TipoEventoOperacao;
  severidade: SeveridadeEvento;
  resumo: string;
  payload?: Record<string, unknown> | null;
  criadoEm: string;
}

// SPEC_D - Checklist Documental (E6)
export type EtapaExigencia = 'CREDITO' | 'PRE_ANALISE_JURIDICA' | 'AVALIACAO' | 'CONTRATACAO' | 'REGISTRO';
export type StatusItemChecklist = 'PENDENTE' | 'ENTREGUE' | 'VENCIDO' | 'AUSENTE' | 'NAO_APLICAVEL';

export interface ChecklistItem {
  id: string;
  status: StatusItemChecklist;
  vencimentoEm?: string | null;
  resolvidoEm?: string | null;
  documentoId?: string | null;
  documento?: Documento | null;
  // SPEC_LAYOUT_UX_V1 (E2) - checklist pertence à proposta; null = operação
  // sem proposta CAIXA ativa (exceção do backfill, ou ainda sem programa).
  propostaId?: string | null;
  regra: {
    codigo: string;
    bloco: string;
    natureza: 'DOCUMENTO' | 'TRAVA' | 'PROCESSO';
    descricaoExigencia: string;
    etapaExigencia: EtapaExigencia;
    trava: boolean;
    // SPEC_LAYOUT_UX_V1 (E2) - exigência universal (documento pessoal, estado
    // civil, renda) - única coisa exibida para banco sem motor normativo.
    nucleoComum: boolean;
    // SPEC_CHECKLIST_INTELIGENTE_V1_1 (E3) - balde B: item vira grupo próprio
    // "Checagens externas" em vez de entrar no agrupamento por etapa.
    categoria: 'PADRAO' | 'CHECAGEM_EXTERNA';
  };
  // SPEC_CHECKLIST_INTELIGENTE_V1_1 (E3) - histórico de checagens externas
  // (só populado quando regra.categoria=CHECAGEM_EXTERNA); mais recente primeiro.
  checagensExternas?: {
    id: string;
    veredito: 'OK' | 'RESTRICAO' | 'OBSERVACAO';
    observacao?: string | null;
    checadoEm: string;
    usuario: { id: string; nome: string };
  }[];
  // SPEC_LAYOUT_UX_V1 (E5.2, lacuna fechada em 07/07/2026 na
  // SPEC_CHECKLIST_INTELIGENTE_V1_1 E4) - true se regra.etapaExigencia já foi
  // alcançada pela esteira atual da operação. false = fase futura, some por
  // padrão num grupo colapsado com contador.
  faseAlcancada: boolean;
  // SPEC_CHECKLIST_INTELIGENTE_V1_1 (UPDATE de dado, 07/07/2026) - nota livre
  // por item; hoje só usada por T1.G5 (confirmar CLT×estatutário).
  detalhe?: { nota?: string } | null;
}

export type PapelParticipante = 'PROPONENTE' | 'COOBRIGADO' | 'CONJUGE' | 'ANUENTE';

export interface Participante {
  id: string;
  operacaoId: string;
  parteId: string;
  papel: PapelParticipante;
  percentualComposicaoRenda: number | null;
  parte: Pessoa;
}

// SPEC_LAYOUT_UX_V1 (E4/E5) - pendências de informação
export type EstadoCivil = 'SOLTEIRO' | 'CASADO' | 'DIVORCIADO' | 'VIUVO' | 'UNIAO_ESTAVEL' | 'SEPARADO_FATO' | 'DIVORCIADO_SEM_PARTILHA' | 'VIUVO_INVENTARIO_ABERTO';
export type CondicaoImovel = 'NOVO' | 'USADO';
export type OrigemPendencia = 'ISOLVE' | 'CCV' | 'CLIENTE' | 'INDEFINIDA';

export interface Ocupacao {
  id: string;
  codigo: string;
  descricao: string;
  grupo?: string | null;
  anexo: string;
}

export interface PendenciaInformacao {
  id: string;
  campo: string;
  campoLabel: string;
  origemEsperada: OrigemPendencia;
  faseDisponibilidade?: string | null;
  participanteNome?: string;
  createdAt: string;
}

export interface Operacao {
  id: string;
  numeroProcesso: string;     // ex: "5A-2024-001"
  origem: OrigemPrefixo;
  esteira: Esteira;
  // SPEC_LAYOUT_UX_V1 (E1) - enum bruto do Prisma (ENTRADA/CREDITO/OPERACIONAL/
  // REGISTRO). Necessário para posicionar o card corretamente dentro do bucket
  // visual mesclado "Crédito" (esteira=1 agrupa ENTRADA e CREDITO).
  esteiraAtualEnum: EsteiraEnum;
  // SPEC_LAYOUT_UX_V1 (E1) - gate "Seleção de Banco": preenchido quando o
  // cliente já escolheu o banco vencedor (pré-requisito da transição Crédito→Jurídico).
  bancoSelecionadoId: string | null;
  status: StatusOperacao;
  clienteConduz: boolean;     // sub-status esteira Registro (bucket 3)
  subStatus: number;          // 1-based dentro da esteira atual (ENTRADA ou CREDITO - ver esteiraAtualEnum)

  // Partes
  compradores: Pessoa[];
  vendedores: Pessoa[];
  enNome?: string;            // Executivo de Negócios (Quinto Andar)

  // Imóvel / financeiro
  // Sessão 7B: null quando não informado no cadastro (card exibe "-")
  valorImovel: number | null;
  valorFinanciamento: number | null;
  // Valores desejados pelo cliente no momento 1 (Cadastro). iSolve: dadosOperacao.
  valorImovelDesejado: number | null;
  valorFinanciamentoDesejado: number | null;
  enderecoImovel?: string;

  // Bancos
  bancos: Banco[];
  bancoPrincipalId?: string;

  // Responsáveis internos
  responsavelNome: string;
  responsavelEmail?: string;

  // SharePoint
  urlPastaSharePoint?: string;

  // Congelamento
  motivoCongelamento?: string;
  dataCongelamento?: string;
  prazoRetornoCongelamento?: string;

  // Timestamps
  createdAt: string;
  updatedAt: string;
  diasNaEsteira: number;

  // Derivados (badges)
  documentosPendentes: number;
  mensagensNaoLidas: number;
  tarefasPendentes: number;

  // Detalhes (carregados no modal)
  documentos?: Documento[];
  mensagens?: Mensagem[];
  tarefas?: Tarefa[];
  timeline?: TimelineEvento[];

  // Detalhes extras do GET /:id
  offerId?: string;
  // idCstimob - id da operação no iSolve (link de análise de crédito na aba Msgs)
  idCstimob?: number | null;
  imovelDetalhe?: ImovelDetalhe;
  movimentacoes?: MovimentacaoEsteira[];
  // Estado "pré-CCV" é derivado - null = pré-CCV (SPEC 3, caminho da exceção)
  ccvDataAssinatura?: string | null;
  // SPEC S3 (E4) - presença habilita o botão "Criticar CCV" na aba Docs
  ccvArquivoId?: string;

  // SPEC S1 (E3/E4) - farol do board, presente só no GET /operacoes (lista/kanban)
  eventosNaoVistos?: number;
  temAcionavelNaoVisto?: boolean;

  // SPEC_D - motores em dois tempos (E4/E6): chip provisório/definitivo no modal
  esteiraAltoPadraoProvisorio?: boolean | null;
  esteiraAltoPadraoDefinitivo?: boolean | null;
  vedacaoImovelProvisoria?: boolean | null;
  vedacaoImovelDefinitiva?: boolean | null;
  limiteFgtsExcedeProvisorio?: boolean | null;
  limiteFgtsExcedeDefinitivo?: boolean | null;
}

export interface ColunasKanban {
  0: Operacao[];
  1: Operacao[];
  2: Operacao[];
  3: Operacao[];
}

// Sub-status por ENUM BRUTO (não por bucket visual) - evita a ambiguidade do
// bucket "Crédito" (esteira=1) agrupar ENTRADA e CREDITO. Espelha
// backend/scripts/oneoff/seed-etapas-sub-esteira.ts (fonte de verdade real é a tabela
// etapas_sub_esteira via API; isto é só cache de display do KanbanCard).
export const SUB_STATUS_CONFIG_POR_ENUM: Record<EsteiraEnum, ReadonlyArray<{ label: string }>> = {
  // COMERCIAL (pré-CCV): sem cache hardcoded — as etapas vêm só da tabela
  // configurável via API (gerenciador de etapas). O mini-label do KanbanCard
  // cai no fallback '-' até o SubBoard carregar os labels reais.
  COMERCIAL: [],
  ENTRADA: [
    { label: 'Identificada no iSolve' },
    { label: 'Importando documentação' },
    { label: 'Docs pendentes' },
    { label: 'Primeiro contato realizado' },
    { label: 'CRM preenchido' },
  ],
  CREDITO: [
    { label: 'Recebida' },
    { label: 'Solicitando informações' },
    { label: 'Documentação enviada ao banco' },
    { label: 'Em pré-análise' },
    { label: 'Regularização solicitada' },
    { label: 'Tratativas ativas' },
    { label: 'Aprovado - aguardando ok cliente' },
    { label: 'Seleção de Banco' },
  ],
  OPERACIONAL: [
    { label: 'Recebida' },
    { label: 'Avaliação do imóvel' },
    { label: 'Análise jurídica' },
    { label: 'Aguardando conformidade CEF' },
    { label: 'FGTS em andamento' },
    { label: 'Interveniente quitante' },
    { label: 'Minuta solicitada' },
    { label: 'Assinaturas em andamento' },
    { label: 'Contrato assinado' },
  ],
  REGISTRO: [
    { label: 'Recebida' },
    { label: 'Submetido ao RI' },
    { label: 'Taxas enviadas ao cliente' },
    { label: 'Em pré-análise RI' },
    { label: 'Exigência - em correção' },
    { label: 'Registrado' },
    { label: 'Recursos liberados' },
  ],
};

// ── Conector iSolve - Fila de Divergências (SPEC_CONECTOR_INCREMENTAL v1.1) ──

// SPEC S3 (03/07/2026) + SPEC_D (05/07/2026) ampliaram o enum TipoDivergencia no Prisma
// (CRITICA_CCV, DOIS_TEMPOS_*) - tipo do frontend estava desatualizado (só os 3 originais
// do conector incremental); ampliado aqui para bater com o schema (necessário para a aba
// Crítica CCV filtrar por tipo com segurança de tipos, UI pré-demo 05/07/2026).
export type TipoDivergencia =
  | 'INATIVACAO_COMPRADOR'
  | 'DIVERGENCIA_CAMPO'
  | 'REGISTRO_NOVO'
  | 'CRITICA_CCV'
  | 'DOIS_TEMPOS_ASSINATURA'
  | 'DOIS_TEMPOS_ALTO_PADRAO'
  | 'DOIS_TEMPOS_VEDACAO_LIMITE';
export type StatusDivergencia = 'PENDENTE' | 'APROVADO' | 'REJEITADO';

export interface DivergenciaConector {
  id: string;
  criadoEm: string;
  resolvidoEm?: string | null;
  tipo: TipoDivergencia;
  entidade: string;       // "Operacao" | "PropostaBancaria" | "Imovel" | "Parte"
  entidadeId: string;
  campo: string;
  valorAtualCRM?: string | null;
  valorPropostoISolve?: string | null;
  status: StatusDivergencia;
  operadorId?: string | null;
  observacao?: string | null;
  // ── Identificação da operação (E1, SPEC_DIVERGENCIAS_V2) ──────────────────
  operacaoId?: string | null;
  operacaoNumero?: string | null;
  compradorPrincipalNome?: string | null;
  resolucaoAmbigua?: boolean;
}

// ── Fila de Homologação de Enquadramento (SPEC_BLOCO6, 19/07/2026) ───────────

export type DecisaoMotor = 'FAIXA_1' | 'FAIXA_2' | 'FAIXA_3' | 'CLASSE_MEDIA' | 'PRO_COTISTA' | 'SBPE' | 'FORA_DE_ESCOPO';
export type FaixaEnquadramento = 'FAIXA_1' | 'FAIXA_2' | 'FAIXA_3' | 'CLASSE_MEDIA';
export type VeredictoHomologacao = 'APROVA' | 'AJUSTA' | 'REJEITA';

export interface ParametroUsadoHomologacao {
  chave: string;
  fonte: string;
  vigencia: string;
}

export interface ItemFilaHomologacao {
  id: string;
  propostaBancariaId: string;
  operacaoId: string;
  operacaoNumero: string;
  entrada: {
    rendaFamiliarBruta: number | null;
    cidade: string | null;
    estado: string | null;
    codigoIbge: string | null;
    valorImovel: number | null;
  };
  saida: {
    decisaoMotor: DecisaoMotor;
    faixaEnquadramento: FaixaEnquadramento | null;
    motivos: string[];
    parametrosUsados: ParametroUsadoHomologacao[];
    dataReferencia: string;
  };
  veredicto: VeredictoHomologacao | null;
  faixaAjustada: FaixaEnquadramento | null;
  justificativa: string | null;
  autor: { id: string; nome: string } | null;
  decididoEm: string | null;
  contadorRodadasSyncAutomatico: number;
  createdAt: string;
}

// ── Buscar no iSolve - caminho da exceção pré-CCV, universo de OPERAÇÕES (SPEC 3.1) ──

export interface ItemSemCcv {
  idCstimob: string;
  nome: string | null;
  // CPF integral formatado (XXX.XXX.XXX-XX) - REFINO 02/07/2026: ferramenta interna
  // autenticada, decisão do Emerson de remover o mascaramento (campo não masca mais).
  cpf: string | null;
  situacao: string;          // rótulo quando conhecido; fallback "Situação {n}"
  subestado: string | null;  // não confirmado no payload real (INT-027 adendo) - sempre null por ora
  offer: string | null;      // só existe após a 1ª proposta (v1) - null nesta fase
  valor: number | null;      // idem - não disponível nesta listagem
  parceiro: string | null;
  dataEntrada: string | null;
}

export interface ResultadoListaSemCcv {
  itens: ItemSemCcv[];
  truncado: boolean;
}

export const TIPO_DIVERGENCIA_LABEL: Record<TipoDivergencia, string> = {
  INATIVACAO_COMPRADOR:       'Inativação',
  DIVERGENCIA_CAMPO:          'Campo divergente',
  REGISTRO_NOVO:              'Registro novo',
  CRITICA_CCV:                'Crítica CCV',
  DOIS_TEMPOS_ASSINATURA:     'Dois tempos - assinatura',
  DOIS_TEMPOS_ALTO_PADRAO:    'Dois tempos - alto padrão',
  DOIS_TEMPOS_VEDACAO_LIMITE: 'Dois tempos - vedação/limite',
};

// ── Mapas de exibição (SPEC_REDESIGN_VISUAL_v1 §1, remapeado SPEC_LAYOUT_UX_V1/E1) ──
// Labels e cores APENAS para renderização - nunca gravar no banco.
// Board vira 3 esteiras: "Crédito" agrupa ENTRADA (Cadastro, 1ª etapa) + CREDITO
// (análise bancária + Seleção de Banco). Nome interno da esteira Jurídico no
// banco (enum Prisma): "OPERACIONAL".
export const ESTEIRA_DISPLAY: Record<Esteira, string> = {
  0: 'Comercial',
  1: 'Crédito',
  2: 'Jurídico',
  3: 'Registro',
};

export const ESTEIRA_COLOR: Record<Esteira, string> = {
  0: '#0284C7',  // azul-céu - Comercial (pré-CCV)
  1: '#C2500A',  // laranja  - Crédito
  2: '#0F6E56',  // verde    - Jurídico
  3: '#185FA5',  // azul     - Registro
};

// Origem → faixa e badge
export const ORIGEM_STYLE: Record<string, { badgeBg: string; badgeTxt: string; stripe: string }> = {
  '5A':  { badgeBg: '#E6F1FB', badgeTxt: '#0C447C', stripe: '#2E75B6' },
  'S4':  { badgeBg: '#E1F5EE', badgeTxt: '#085041', stripe: '#0F6E56' },
  'VAR': { badgeBg: '#EEEDFE', badgeTxt: '#3C3489', stripe: '#6D64D8' },
};

// SPEC_LAYOUT_UX_V1 (E1) - 3 esteiras. Bucket 1 "Crédito" herda a config da
// antiga esteira 2 (CREDITO/Giulia) - é a etapa que domina o bucket mesclado
// (8 das 13 colunas); Cadastro (ex-ENTRADA) vira só a fase inicial dele.
export const ESTEIRAS_CONFIG = {
  0: {
    nome: 'Comercial',
    responsavel: 'pré-CCV',
    cor: 'sky',
    badgePrincipal: 'mensagensNaoLidas',
    badgeLabel: 'msgs não lidas',
  },
  1: {
    nome: 'Crédito',
    responsavel: 'Giulia',
    cor: 'yellow',
    badgePrincipal: 'mensagensNaoLidas',
    badgeLabel: 'msgs não lidas',
  },
  2: {
    nome: 'Jurídico',
    responsavel: 'Géssica · Bruno',
    cor: 'purple',
    badgePrincipal: 'tarefasPendentes',
    badgeLabel: 'tarefas pendentes',
  },
  3: {
    nome: 'Registro',
    responsavel: 'Bruno',
    cor: 'green',
    badgePrincipal: 'documentosPendentes',
    badgeLabel: 'docs pendentes',
  },
} as const;
